import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

interface ConfigData {
  // tax: string;
  minimumBuyForMeCost: string;
  buyForMeRate: string;
  shipForMeRate: string;
  minimumPartialPaymentPercentage: string;
  enableBuyForMe: boolean;
  enableShipForMe: boolean;
  approvedOrderResetHours: string;
  minimumOrderWeight: string;
  onboardingStepOrder: string;
  maximumOrdersPerCreate: string;
}

interface OnboardingStepItem {
  id: number;
  labelKey: string;
}

const DEFAULT_ONBOARDING_STEPS: OnboardingStepItem[] = [
  { id: 1, labelKey: 'onboarding.steps.profile' },
  { id: 2, labelKey: 'onboarding.steps.createOrder' },
  { id: 3, labelKey: 'onboarding.steps.dashboard' },
  { id: 4, labelKey: 'onboarding.steps.orders' },
  { id: 5, labelKey: 'onboarding.steps.shipForMe' },
];

const DEFAULT_ONBOARDING_ORDER = DEFAULT_ONBOARDING_STEPS.map(step => step.id);

const normalizeOnboardingOrder = (orderStr: string | undefined): number[] => {
  const parsed = (orderStr || '')
    .split(',')
    .map(id => parseInt(id.trim(), 10))
    .filter(id => !Number.isNaN(id));

  const uniqueParsed = Array.from(new Set(parsed));
  const missingDefaults = DEFAULT_ONBOARDING_ORDER.filter(id => !uniqueParsed.includes(id));

  return [...uniqueParsed, ...missingDefaults];
};

export const Configuration: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('configuration');
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState<ConfigData>({
    // tax: "",
    minimumBuyForMeCost: "",
    buyForMeRate: "",
    shipForMeRate: "",
    minimumPartialPaymentPercentage: "",
    enableBuyForMe: true,
    enableShipForMe: true,
    approvedOrderResetHours: "",
    minimumOrderWeight: "",
    onboardingStepOrder: "1,2,3,4,5",
    maximumOrdersPerCreate: "10",
  });

  const [onboardingSteps, setOnboardingSteps] = useState<OnboardingStepItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);

  function toLowerFirstLetter(word: string): string {
    if (!word) return word; // Return unchanged if empty or null
    return word.charAt(0).toLowerCase() + word.slice(1);
  }

  // Fetch config details
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await adminApi.call('/config', 'GET');

        if (!response.success) {
          showErrorToast(response.error || "Failed to fetch configuration");
          return;
        }

        const data = response.data;

        const mappedData = data.items.reduce((acc: any, item: any) => {
          let parsedValue = item.value;
          if (item.name === "EnableBuyForMe" || item.name === "EnableShipForMe") {
            parsedValue = item.value === "true";
          }
          acc[toLowerFirstLetter(item.name)] = parsedValue;
          return acc;
        }, {});

        setFormData((prevData) => {
          const newData = {
            ...prevData,
            ...mappedData,
          };
          
          // Initialize sorted steps
          const orderArr = normalizeOnboardingOrder(mappedData.onboardingStepOrder);
          const sortedSteps = [...DEFAULT_ONBOARDING_STEPS].sort((a, b) => {
            const indexA = orderArr.indexOf(a.id);
            const indexB = orderArr.indexOf(b.id);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
          setOnboardingSteps(sortedSteps);

          newData.onboardingStepOrder = orderArr.join(',');

          return newData;
        });
      } catch (error: any) {
        showErrorToast("Error: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [token]);

  // Handle drag and drop
  const handleDragStart = (index: number) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === index) return;

    const newSteps = [...onboardingSteps];
    const draggedItem = newSteps[draggedItemIndex];
    newSteps.splice(draggedItemIndex, 1);
    newSteps.splice(index, 0, draggedItem);
    
    setDraggedItemIndex(index);
    setOnboardingSteps(newSteps);
    
    // Update formData with new order
    setFormData(prev => ({
      ...prev,
      onboardingStepOrder: newSteps.map(s => s.id).join(',')
    }));
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const updateConfig = async () => {
    const maximumOrdersPerCreate = Number(formData.maximumOrdersPerCreate);
    if (!Number.isInteger(maximumOrdersPerCreate) || maximumOrdersPerCreate <= 0) {
      showErrorToast(t('validation.maximumOrdersPerCreate'));
      return;
    }

    setUpdating(true);

    try {
      // Ensure values are correctly formatted before sending
      const data = [
        // { name: "Tax", value: formData.tax.toString() },
        { name: "BuyForMeRate", value: formData.buyForMeRate.toString() },
        { name: "ShipForMeRate", value: formData.shipForMeRate.toString() },
        {
          name: "MinimumBuyForMeCost",
          value: formData.minimumBuyForMeCost.toString(),
        },
        {
          name: "MinimumPartialPaymentPercentage",
          value: formData.minimumPartialPaymentPercentage.toString(),
        },
        { name: "EnableBuyForMe", value: formData.enableBuyForMe ? "true" : "false" },
        { name: "EnableShipForMe", value: formData.enableShipForMe ? "true" : "false" },
        { name: "ApprovedOrderResetHours", value: formData.approvedOrderResetHours.toString() },
        { name: "MinimumOrderWeight", value: formData.minimumOrderWeight.toString() },
        { name: "OnboardingStepOrder", value: formData.onboardingStepOrder },
        { name: "MaximumOrdersPerCreate", value: maximumOrdersPerCreate.toString() },
      ];

      const response = await adminApi.call('/config', 'POST', data);

      if (!response.success) {
        showErrorToast(response.error || t('messages.updateError'));
        return;
      }

      showSuccessToast(t('messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('messages.updateError') + `: ${error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const breadcrumbs = [
    { label: t('breadcrumbs.settings') },
    { label: t('breadcrumbs.configuration') }
  ];

  if (loading) {
    return (
      <PageLayout
        title={t('title')}
        subtitle={t('subtitle')}
        breadcrumbs={breadcrumbs}
      >
        <div className="text-center mt-4">{t('messages.loading')}</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('title')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4 mb-4">
        <div className="row g-3">
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.buyForMeRate')}
            </label>
            <input
              type="number"
              step="0.01"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="buyForMeRate"
              value={formData.buyForMeRate}
              onChange={handleInputChange}
              placeholder={t('placeholders.buyForMeRate')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.shipForMeRate')}
            </label>
            <input
              type="number"
              step="0.01"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="shipForMeRate"
              value={formData.shipForMeRate}
              onChange={handleInputChange}
              placeholder={t('placeholders.shipForMeRate')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.minimumBuyForMeCost')}
            </label>
            <input
              type="number"
              step="0.01"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="minimumBuyForMeCost"
              value={formData.minimumBuyForMeCost}
              onChange={handleInputChange}
              placeholder={t('placeholders.minimumBuyForMeCost')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.minimumPartialPaymentPercentage')}
            </label>
            <input
              type="number"
              step="0.01"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="minimumPartialPaymentPercentage"
              value={formData.minimumPartialPaymentPercentage}
              onChange={handleInputChange}
              placeholder={t('placeholders.minimumPartialPaymentPercentage')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.approvedOrderResetHours')}
            </label>
            <input
              type="number"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="approvedOrderResetHours"
              value={formData.approvedOrderResetHours}
              onChange={handleInputChange}
              placeholder={t('placeholders.approvedOrderResetHours')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.minimumOrderWeight')}
            </label>
            <input
              type="number"
              step="0.01"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="minimumOrderWeight"
              value={formData.minimumOrderWeight}
              onChange={handleInputChange}
              placeholder={t('placeholders.minimumOrderWeight')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.maximumOrdersPerCreate')}
            </label>
            <input
              type="number"
              min="1"
              step="1"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="maximumOrdersPerCreate"
              value={formData.maximumOrdersPerCreate}
              onChange={handleInputChange}
              placeholder={t('placeholders.maximumOrdersPerCreate')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6 d-flex align-items-center mt-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="enableBuyForMe"
                name="enableBuyForMe"
                checked={formData.enableBuyForMe}
                onChange={handleInputChange}
              />
              <label className="form-check-label ms-2" htmlFor="enableBuyForMe">
                {t('fields.enableBuyForMe')}
              </label>
            </div>
          </div>
          <div className="col-md-6 d-flex align-items-center mt-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="enableShipForMe"
                name="enableShipForMe"
                checked={formData.enableShipForMe}
                onChange={handleInputChange}
              />
              <label className="form-check-label ms-2" htmlFor="enableShipForMe">
                {t('fields.enableShipForMe')}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-lg border-0 rounded-3 p-4">
        <h4 className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{t('onboarding.title')}</h4>
        <p className={`text-muted mb-4 ${isRTL ? 'text-end' : 'text-start'}`}>{t('onboarding.subtitle')}</p>
        
        <div className="onboarding-steps-list">
          {onboardingSteps.map((step, index) => (
            <div
              key={step.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`d-flex align-items-center p-3 mb-2 bg-light rounded border ${draggedItemIndex === index ? 'opacity-50' : ''}`}
              style={{ cursor: 'move' }}
            >
              <div className="me-3">
                <i className="fa fa-bars text-muted"></i>
              </div>
              <div className="flex-grow-1">
                <strong>{t(step.labelKey)}</strong>
              </div>
              <div className="badge bg-secondary">
                {index + 1}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-4 border-top pt-4">
          <ActionButton
            variant="success"
            onClick={updateConfig}
            loading={updating}
            disabled={updating}
          >
            {updating ? t('actions.updating') : t('actions.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
